/**
 * Traducciones en español para payments-ui (Fase 1).
 *
 * Todas las claves están namespaceadas bajo `paymentsUI.*` para evitar
 * colisiones con otras partes del sistema (checkout, app, etc.).
 *
 * Patrón: Sigue la estructura de componentes del proyecto.
 * Soporta interpolación: {{ param }}
 */

import { isDesktop } from "../platform";

export const es = {
  paymentsUI: {
    // ====== Checkout Stepper ======
    checkoutStepper: {
      ariaLabel: 'Pasos del checkout',
      cartStep: 'Carrito',
      deliveryStep: 'Entrega',
      paymentStep: 'Pago',
    },

    // ====== Header ======
    header: {
      title: 'Seleccioná tu método de pago',
      title2Cards: 'Método de pago',
      addButton: '+ Agregar',
      addButtonLabel: 'Agregar nuevo método de pago',
    },

    // ====== Payment Method Card ======
    paymentMethodCard: {
      issuerLabel: '{{issuerName}}',
      cardNumber: '**** {{last4}}',
      holderLabel: '{{holderName}}',
      networkLabel: '{{network}}',
      networkVariant: '{{networkVariant}}',
      ariaLabel: '{{issuerName}} tarjeta terminada en {{last4}}',
    },

    // ====== MercadoPago Card ======
    mercadopago: {
      title: 'Mercado Pago',
      subtitle: 'Pagá en cuotas sin tarjetas',
      ariaLabel: 'Mercado Pago, pagá en cuotas sin tarjetas',
      ariaPressed: 'Opción Mercado Pago {{pressed, select, true {seleccionada} false {no seleccionada}}}',
    },

    // ====== Add Payment Method Card ======
    addPaymentCard: {
      title: 'Agregá tu tarjeta de crédito / débito',
      ariaLabel: 'Agregá tu tarjeta de crédito o débito',
    },

    // ====== Carousel ======
    carousel: {
      scrollHint: 'Deslizá para ver más opciones ›',
      scrollHintWeb: 'Deslizá para ver más opciones',
    },

    // ====== CVV Input ======
    cvvInput: {
      label: 'CVV',
      editLabel: 'Código de seguridad *',
      placeholder: 'CVV',
      error: 'CVV debe tener 3 o 4 dígitos',
      ariaLabel: 'Código de seguridad (CVV)',
    },

    // ====== Installment Selector & Option ======
    installment: {
      title: 'Seleccioná las cuotas de pago',
      loading: 'Cargando cuotas...',
      unavailable: 'No pudimos obtener las cuotas. Intentá de nuevo en unos segundos.',
      label: 'Cuota {{installment}} de {{total}}: {{amount}}',
      noInstallment: 'Pago de contado',
      ariaLabel: 'Cuota {{installment}} de {{total}}: {{amount}}',
      singular: '{{months}} cuota',
      pluralWithSuffix: '{{months}} cuotas mensuales',
      pluralShort: '{{months}} cuotas',
      selectAriaLabel: 'Seleccionar {{months}} cuota',
      selectAriaLabelPlural: 'Seleccionar {{months}} cuotas',
    },

    // ====== Payment Method Selector ======
    selector: {
      title: 'Método de pago',
      description: 'Selecciona cómo deseas pagar',
    },

    // ====== Amount Input ======
    amountInput: {
      placeholder: 'Ingresar monto',
      ariaLabel: 'Monto a abonar con esta tarjeta',
    },

    // ====== Installment Picker (dropdown colapsable) ======
    installmentPicker: {
      placeholder: 'Seleccionar cuotas',
      title: 'Seleccionar cuotas',
      ariaLabel: 'Seleccionar cuotas de pago',
    },

    // ====== Split Payment (2 tarjetas) ======
    splitPayment: {
      addSecondCard: '+ Pagar con 2 tarjetas',
      removeSecondCard: '− Pagar con 1 tarjeta',
      helperText: 'Seleccioná una tarjeta e ingresa el monto que abonaras con ella',
      firstCardLabel: 'Primera tarjeta',
      secondCardLabel: 'Segunda tarjeta',
      amountLabel: 'Monto a pagar con esta tarjeta',
      sameTypeNotice: 'Solo se pueden combinar tarjetas del mismo tipo. (Ejemplo: 2 tarjetas de crédito)',
      addNewCard: 'Agregar nueva tarjeta',
      installmentSectionTitle: 'Seleccioná las cuotas de pago de la {{cardLabel}}',
    },

    // ====== Resultado del pago ======
    result: {
      approved: '¡Pago aprobado!',
      pending: 'Estamos confirmando tu pago',
      approvedLead: 'Tu compra fue confirmada con éxito.',
      redirecting: 'Te estamos llevando a la página de confirmación…',
      orderNumber: 'Número de pedido: {{orderId}}',
    },

    // ====== Cuotas (pre-initiate) ======
    installments: {
      interestLabel: '{{percent}}% de interés',
      noInterestBadge: 'CUOTAS SIN INTERÉS',
    },

    // ====== Checkout Total Footer ======
    footer: {
      total: 'Total',
      submit: 'Finalizar compra',
      submitting: 'Procesando...',
      showDetail: 'VER DETALLE',
      hideDetail: 'CERRAR DETALLE',
      summaryTitle: 'Resumen de tu pedido',
      products: 'Productos',
      shipping: 'Costo de entrega',
      discounts: 'Descuentos',
      close: 'Cerrar',
    },

    // ====== Card Number Input ======
    cardNumberInput: {
      label: 'Número de tarjeta *',
      placeholder: 'XXXX XXXX XXXX XXXX',
      ariaLabel: 'Número de tarjeta',
    },

    // ====== Card Preview (Agregar tarjeta) ======
    cardPreview: {
      placeholderExpiry: 'XX/XX',
      placeholderNumber: 'XXXX XXXX XXXX XXXX',
      placeholderHolderName: 'NOMBRE APELLIDO',
      placeholderIssuerName: 'Banco Santander Río',
      placeholderNetwork: 'Visa',
      placeholderNetworkVariant: 'Débito',
    },

    // ====== Add Card Form (Agregar método de pago) ======
    addCardForm: {
      back: '‹ Volver',
      title: 'Agregar método de pago',
      expiryLabel: 'Vencimiento *',
      expiryPlaceholder: 'MM/AA',
      securityLabel: 'Código de seguridad *',
      securityPlaceholder: 'Código de seguridad',
      nameLabel: 'Nombre y apellido tal cual figura en la tarjeta *',
      namePlaceholder: 'Nombre y apellido',
      dniLabel: 'DNI *',
      dniPlaceholder: 'XX XXX XXX',
      save: 'Guardar tarjeta',
      saving: 'Guardando…',
      errors: {
        cardNumber: 'Ingresá un número de tarjeta válido (15 a 19 dígitos).',
        expiration: 'Revisá la fecha de vencimiento: no puede estar vencida.',
        holderName: 'Ingresá el nombre como figura en la tarjeta.',
        securityCode: 'Ingresá un código de seguridad de 3 o 4 dígitos.',
        document: 'Ingresá un DNI válido, sin puntos.',
        sdkUnavailable: 'No pudimos cargar el servicio de pagos. Intentá nuevamente.',
        timeout: 'La operación tardó demasiado. Intentá nuevamente.',
        invalidCard: 'No pudimos validar la tarjeta. Revisá los datos e intentá nuevamente.',
        unknown: 'No pudimos procesar la tarjeta. Intentá nuevamente.',
        saveFailed: 'No pudimos guardar la tarjeta. Intentá nuevamente.',
      },
    },

    // ====== Card Status Modal (resultado de agregar tarjeta) ======
    cardStatusModal: {
      invalidTitle: 'Tarjeta inválida',
      expiredTitle: 'Tarjeta expirada',
      successTitle: 'Tarjeta agregada con éxito',
      paymentErrorTitle: 'No pudimos procesar tu pago',
      doneButton: 'Listo',
      closeAriaLabel: 'Cerrar',
    },

    paymentFailure: {
      cardTitle: 'No pudimos validar tu tarjeta. Revisá los datos y volvé a intentar.',
      paymentTitle: 'No pudimos procesar tu pago',
      paymentDetail: 'Revisá los datos de tu tarjeta o probá con otro medio de pago.',
      hint:
        'Si el rechazo continúa, te sugerimos comunicarte con tu banco o entidad emisora de la tarjeta.',
      action: 'Revisar datos',
    },

    // ====== Confirmar eliminación de tarjeta ======
    confirmDeleteCardModal: {
      title: '¿Querés eliminar la tarjeta terminada en {{last4}}?',
      titleWeb: '¿Eliminar esta tarjeta?',
      descriptionWebBefore: 'La tarjeta terminada en ',
      descriptionWebAfter: ' se quitará de tus medios de pago guardados.',
      confirmButton: 'Eliminar',
      cancelButton: 'Cancelar',
      closeAriaLabel: 'Cerrar',
    },

    // ====== Address Info Box ======
    addressInfo: {
      deliveryAddress: 'Dirección de entrega',
      billingAddress: 'Dirección de facturación',
      cardBillingAddressTitle: 'La dirección de la factura de la tarjeta es',
      sameAsDelivery: 'Usar la misma dirección de entrega',
      street: 'Calle',
      number: 'Número',
      apartment: 'Departamento / Piso',
      neighborhood: 'Barrio',
      city: 'Ciudad',
      zipCode: 'Código postal',
      province: 'Provincia',
    },

    // ====== Gift Card Promo ======
    sessionExpired: {
      title: 'No pudimos cargar tu carrito',
      message:
        'Tu sesión no es válida o ha expirado.\nPara continuar, iniciá sesión nuevamente y seleccioná los productos que querés comprar.',
      goHome: 'Ir al inicio',
      signIn: 'Iniciar sesión',
      needHelp: '¿Necesitás ayuda?',
      contact: 'Contactanos',
    },

    giftCard: {
      prompt: '¿Tenés un código de regalo?',
      label: 'Código de regalo',
      description: 'Ingresar el código de tu Código de regalo',
      addButtonLabel: 'Agregar código de regalo',
      modalLabel: 'Modal de código de regalo',
      inputPlaceholder: 'Ingresá tu código',
      applyButton: 'Aplicar',
      cancelButton: 'Cancelar',
      acceptButton: 'Aceptar',
      helperText: 'Podés usar hasta {{max}} códigos por compra',
      removeCode: 'Eliminar código {{code}}',
      errors: {
        empty: 'Por favor ingresá un código',
        invalid: 'Código incorrecto',
        duplicate: 'Este código ya fue ingresado',
        maxCodes: 'Alcanzaste el límite de códigos',
        invalidFormat: 'El formato del código no es válido',
        timeout: 'La operación tardó demasiado. Intentá nuevamente.',
        generic: 'No pudimos procesar el código. Intentá nuevamente.',
        outOfService: 'El código de ingresado se encuentra fuera de servicio',
        limitReached: 'El código de ingresado alcanzó su límite de uso',
        expired: 'El código de ingresado venció',
      },
    },

    // ====== Error Box ======
    errorBox: {
      title: 'Error',
      description: 'Ocurrió un error al procesar tu pago. Por favor, intentá nuevamente.',
      retryButton: 'Reintentar',
      dismissButton: 'Cerrar',
      genericError: 'Algo salió mal. Intentá nuevamente.',
      selectPaymentMethod: 'Debe seleccionar un método de pago',
      processingError: 'Error al procesar el pago',
      selectInstallments: 'Debe seleccionar las cuotas',
      reAddCard: 'Volvé a ingresar la tarjeta para reintentar.',
      enterSecurityCode: 'Ingresá el código de seguridad de la tarjeta.',
      cardTokenExpired: 'La tarjeta nueva venció por tiempo. Volvé a agregarla para pagar.',
      deleteCardError: 'No pudimos eliminar la tarjeta. Intentá nuevamente.',
      deleteCardSuccess: 'Eliminamos la tarjeta terminada en {{last4}}.',
      preInitiateError: 'No pudimos obtener las cuotas. Intentá nuevamente.',
      paymentRejected: 'El pago fue rechazado. Probá con otra tarjeta.',
      outcomeUnknown:
        'No pudimos confirmar tu pago. No lo repitas: revisá tus pedidos antes de volver a intentar.',
      ariaLabel: 'Error: {{message}}',
    },

    // ====== Checkout web (source=desktop). El modo app no usa estas claves. ======
    web: {
      stepTitle: 'Paso 4 - Pago',
      chooseHowToPay: 'Elegí cómo querés pagar',
      add: 'Agregar',
      methodsTitle: 'Medios de pago',
      addCard: 'Agregar tarjeta',
      addCardEmpty: 'Agregar tarjeta de crédito, débito o prepaga',
      addCardSubtitle: 'Tarjeta de crédito, débito o prepaga',
      cardRow: '{{issuer}} **** {{last4}}',
      cardKind: 'Tarjeta de {{variant}} - {{network}}',
      deleteCard: 'Eliminar tarjeta',
      mercadoPagoRow: 'Mercado Pago',
      mercadoPagoPayWith: 'Pagar con Mercado Pago',
      modoRow: 'Pagar con MODO',
      installmentsTitle: 'Seleccioná las cuotas de pago',
      newCardTitle: 'Nueva tarjeta',
      newCardCancel: 'Cancelar',
      newCardNumber: 'Número de la tarjeta *',
      newCardNumberPlaceholder: isDesktop ? 'xxxx xxxx xxxx xxxx' : '1234 5678 9012 3456',
      newCardName: 'Nombre como figura en la tarjeta *',
      newCardNamePlaceholder: isDesktop ? 'Nombre y apellido' : 'Juan Perez',
      newCardExpiry: 'Vencimiento *',
      newCardExpiryPlaceholder: 'MM/AA',
      newCardSecurity: 'Código de seguridad *',
      newCardSecurityPlaceholder: isDesktop ? 'CVV' : 'Código de seguridad',
      newCardDni: 'DNI *',
      newCardDniPlaceholder: isDesktop ? 'DNI del titular' : '38383838',
      newCardSave: 'Agregar',
      newCardSaveSheet: 'Agregar tarjeta',
      summaryLabel: 'Resumen de tu pedido',
      viewProducts: 'Ver productos',
      summaryProducts: 'Productos ({{count}} artículos)',
      summaryShipping: 'Costo de entrega',
      summaryDiscounts: 'Descuentos',
      summarySaving: 'Estás ahorrando',
      giftBanner: '¿Tenés una tarjeta de regalo? (Giftcard)',
      giftAction: '+ Agregar código',
      securePayments: 'Pagos 100% seguros',
      promoNote: 'En superposición de promociones y/o tasas se otorgará la mejor opción.',
      mercadoPago: {
        finalize: 'Hacé click en **FINALIZAR COMPRA** para pagar. Te redirigiremos a Mercado Pago.',
        login:
          'Te recomendamos **iniciar sesión** en tu cuenta de Mercado Pago para que la transacción quede reflejada en tu actividad.',
        methods: 'Podés pagar con **dinero en cuenta**, “Cuotas sin tarjeta” o tarjeta de débito.',
        refund:
          'En caso de corresponder una devolución, los tiempos y condiciones de la misma dependerán del banco emisor de tu tarjeta.',
      },
    },

    // ====== General / Common ======
    common: {
      loading: 'Cargando...',
      error: 'Error',
      success: 'Éxito',
      tryAgain: 'Intentar de nuevo',
      close: 'Cerrar',
      cancel: 'Cancelar',
      accept: 'Aceptar',
      required: 'Requerido',
    },
  },
};
